import { motion } from 'framer-motion'
import { Package, Heart, Users, TrendingUp, Plus, Grid3X3 } from 'lucide-react'
import { Button } from '../ui/button'

interface EmptyStateProps {
  onStartContributing: () => void
}

export function EmptyState({ onStartContributing }: EmptyStateProps) {
  const features = [
    {
      icon: Package,
      title: "Share Products You Love",
      description: "Contribute purposeful products that make a positive impact"
    },
    {
      icon: Heart,
      title: "Discover & Support",
      description: "Find and support brands aligned with your values"
    },
    {
      icon: Users,
      title: "Build Community",
      description: "Connect with like-minded changemakers"
    },
    {
      icon: TrendingUp,
      title: "Track Your Impact",
      description: "See how your contributions inspire others"
    }
  ]

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl w-full text-center space-y-8"
      >
        {/* Welcome Header */}
        <div className="space-y-4">
          <div className="w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mx-auto flex items-center justify-center">
            <Grid3X3 className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl heading-primary">Welcome to Purpose Collective</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start building your collection of purposeful products and inspire others to make conscious choices
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card border border-border rounded-lg p-6 text-left"
            >
              <feature.icon className="h-8 w-8 text-primary mb-3" />
              <h3 className="font-semibold mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="space-y-4 pt-8"
        >
          <Button 
            size="lg" 
            onClick={onStartContributing}
            className="px-8"
          >
            <Plus className="mr-2 h-5 w-5" />
            Add Your First Product
          </Button>
          <p className="text-sm text-muted-foreground">
            Join our community of conscious consumers and changemakers
          </p>
        </motion.div>

        {/* Sample Categories */}
        <div className="pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground mb-4">Popular categories to explore:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Sustainable Fashion', 'Eco Home', 'Clean Beauty', 'Ethical Tech', 'Zero Waste'].map((category) => (
              <span
                key={category}
                className="px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-sm"
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  )
}